/*
  Протокол общего собрания собственников - поля, строка «в заявлении будет так»
  и окно «Внести протокол» (решение владельца 19.10.2026, вариант В из макетов).

  Одни и те же поля стоят в двух местах: вторым шагом мастера привязки дома
  (`BindWizard.jsx`) и в окне «Внести протокол» у дома, привязанного без
  протокола. Второй копии полей быть не должно: подписи и проверки разошлись
  бы молча, и протокол вносился бы по-разному в зависимости от пути.

  Скан протокола (21.10.2026, макет 5А): до этого дня его здесь не было, потому
  что хранить файл было негде. Теперь окно первым предлагает загрузить файл -
  система читает его у нас, человек сверяет (`ProtocolScanCheck.jsx`) - а ввод
  номера и даты руками остаётся ниже для протокола без файла (макет 5Б).

  Компонент ничего не грузит сам: значения уходят наверх, сохраняет
  `SettingsCourtSections`. Отказ сервера показывается внутри окна.
*/

import { useState } from 'react'

import { today } from '../api/format.js'
import { Field, Notice } from './Common.jsx'
import Modal from './Modal.jsx'
import ProtocolScanCheck from './ProtocolScanCheck.jsx'
import {
  EMPTY_PROTOCOL,
  isEmptyProtocol,
  protocolBody,
  protocolPreview,
} from './protocolValues.js'

export { EMPTY_PROTOCOL, isEmptyProtocol, protocolBody, protocolPreview }

export const PROTOCOL_TITLE = 'Протокол общего собрания собственников'

/* Длина колонки `oss_protocol_number` (миграция 0035): длиннее не примет сервер. */
const NUMBER_LIMIT = 64

export function ProtocolFields({ values, onChange }) {
  return (
    <>
      <p className="muted modal__text">
        С протокола начинается заявление: без него суду не видно, на каком основании вы
        требуете плату.
      </p>
      <div className="row">
        <Field label="Номер протокола" width="xs">
          <input
            className="field__input"
            value={values.protocol_number}
            maxLength={NUMBER_LIMIT}
            onChange={(event) => onChange({ ...values, protocol_number: event.target.value })}
            placeholder="№"
          />
        </Field>
        <Field label="Дата протокола" width="s">
          <input
            className="field__input"
            type="date"
            max={today()}
            value={values.protocol_date}
            onChange={(event) => onChange({ ...values, protocol_date: event.target.value })}
          />
        </Field>
      </div>
      <div className="protocol-preview">
        <span className="protocol-preview__label">В заявлении будет так</span>
        <q>
          …на основании решения общего собрания собственников помещений (
          <mark className="protocol-preview__mark">{protocolPreview(values)}</mark>).
        </q>
      </div>
    </>
  )
}

/*
  Окно «Внести протокол» - тот же второй шаг мастера, но для дома, который уже
  привязан. Дата договора приходит из дома и уходит обратно: сервер
  переписывает основание целиком, и без неё правка протокола стёрла бы её.
*/
export default function ProtocolDialog({ building, onSave, onReadScan, onSaveScan, onClose }) {
  const [values, setValues] = useState(EMPTY_PROTOCOL)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)
  const [scan, setScan] = useState(null)
  const [reading, setReading] = useState(false)

  /* Файл читается на сервере и ничего не сохраняет: сверка - следующим шагом. */
  async function pick(file) {
    if (!file) return
    setReading(true)
    setFailure(null)
    try {
      setScan({ file, reading: await onReadScan(file) })
    } catch (error) {
      setFailure(error)
    } finally {
      setReading(false)
    }
  }

  async function save(event) {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    try {
      await onSave({ ...protocolBody(values), contract_date: building.contract_date ?? null })
      onClose()
    } catch (error) {
      setFailure(error)
      setBusy(false)
    }
  }

  const note = `${building.full_address || building.address} · помещений: ${building.premises_count}`
  /* У дома протокол уже есть - окно только для файла (21.10.2026, ревью). Ручные
     поля начинались пустыми и сохранялись правкой основания, без подтверждения
     замены: номер и дата менялись, а у счёта оставался прежний скан. Сверка скана
     замену подтверждает явно. */
  const scanOnly = Boolean(onReadScan) && building.has_management_basis

  if (scan) {
    return (
      <Modal title={PROTOCOL_TITLE} note={note} onClose={onClose} wide>
        <ProtocolScanCheck
          key={`${scan.file.name}:${scan.file.size}:${scan.file.lastModified}`}
          building={building}
          file={scan.file}
          reading={scan.reading}
          readFailure={failure}
          onPick={pick}
          onSave={onSaveScan}
          onClose={onClose}
        />
      </Modal>
    )
  }

  return (
    <Modal title={PROTOCOL_TITLE} note={note} onClose={onClose} busy={busy}>
      <form onSubmit={save}>
        {onReadScan ? <ScanPicker reading={reading} onPick={pick} scanOnly={scanOnly} /> : null}
        {scanOnly ? null : <ProtocolFields values={values} onChange={setValues} />}
        <Notice error={failure} />
        <div className="row row--tight protocol-actions">
          {scanOnly ? null : (
            <button className="button" type="submit" disabled={busy || isEmptyProtocol(values)}>
              Сохранить протокол
            </button>
          )}
          <button className="button button--quiet" type="button" onClick={onClose}>
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  )
}

function ScanPicker({ reading, onPick, scanOnly }) {
  return (
    <div className="scan-drop">
      <div>
        Загрузите скан или PDF протокола: Арбитр ЖКХ прочитает дату и организацию, а вы сверите
        прочитанное с листом.
      </div>
      <label className="button">
        {reading ? 'Читаем скан…' : 'Выбрать файл'}
        <input
          type="file"
          hidden
          disabled={reading}
          accept=".pdf,.jpg,.jpeg,.png"
          aria-label="Файл протокола"
          onChange={(event) => onPick(event.target.files?.[0])}
        />
      </label>
      <div className="muted">
        {scanOnly
          ? 'Читается у нас, наружу ничего не уходит. Номер и дату без нового файла правят кнопкой «Изменить» в строке дома.'
          : 'Читается у нас, наружу ничего не уходит. Нет файла - внесите номер и дату ниже.'}
      </div>
    </div>
  )
}
